import * as THREE from 'three';
import { model, type ModelKey } from './Assets';
import { createFlatMaterial, createInkMaterial } from '../render/InkMaterial';
import { offsetDir, PLANET_RADIUS, type Paving, type Planet, type PlanetHooks } from './Planet';
import { arcDist, PolarFrame } from './Sphere';
import { faceToward, MARKET, type NpcSpec, type PickupSpec, type ZoneDef } from './Market';
import { createSeededRandom } from '../utils/random';

/**
 * The whole planet is Burnwood's realm. The Grand Bazaar sits at the "north
 * pole"; villages ring it; Mount Cinder, where the dragon sleeps, is on the
 * far side of the world.
 */
export const REALM = new PolarFrame(MARKET);
const deg = (d: number) => (d * Math.PI) / 180;
const P = (theta: number, phi: number) => REALM.dir(theta, deg(phi));

export const DRAGON_ROAD_PHI = 90;
export const PLACES = {
  farms: P(1.05, 205),
  wood: P(1.25, 330),
  wharf: P(1.0, 28),
  lake: P(1.3, 22),
  keep: P(1.5, DRAGON_ROAD_PHI),
  mine: P(1.9, 250),
  peak: MARKET.clone().negate(),
  dungeon: P(Math.PI - 0.6, DRAGON_ROAD_PHI),
};
const MOUNTAIN_R = 25;

const R = PLANET_RADIUS;
const near = (d: THREE.Vector3, c: THREE.Vector3, r: number) => arcDist(d, c) < r;
const noise = (d: THREE.Vector3, f: number) => {
  const s = Math.sin(d.x * f * 12.9898 + d.y * f * 78.233 + d.z * f * 37.719) * 43758.5453;
  return s - Math.floor(s);
};

/** Terrain: Mount Cinder, the lake by the wharf, the farm fields. */
export const realmHooks: PlanetHooks = {
  lift: (d) => {
    let h = 0;
    const m = arcDist(d, PLACES.peak);
    if (m < MOUNTAIN_R) {
      const k = 1 - m / MOUNTAIN_R;
      const ridges = (Math.sin(d.x * 9.1 + d.z * 7.3) + Math.sin(d.y * 11.7 - d.x * 5.2)) * 0.5;
      h += 11 * k * k * (3 - 2 * k) + ridges * 1.6 * k;
      // A flat shelf cut into the slope for the dungeon door.
      const e = arcDist(d, PLACES.dungeon);
      if (e < 7) h *= THREE.MathUtils.smoothstep(e, 3.5, 7);
    }
    const l = arcDist(d, PLACES.lake);
    if (l < 11) h -= 2.4 * (1 - THREE.MathUtils.smoothstep(l, 4, 11));
    return h;
  },
  paint: (d) => {
    const m = arcDist(d, PLACES.peak);
    if (m < MOUNTAIN_R - 2) {
      if (m < 6 && noise(d, 1.4) > 0.55) return 0xe0642a; // ember cracks near the summit
      return noise(d, 0.9) > 0.5 ? 0x5d544f : 0x6c625b;
    }
    if (m < MOUNTAIN_R + 2) return 0x8c7f72;
    const f = arcDist(d, PLACES.farms);
    if (f < 11 && f > 3.5) {
      // Crop rows: stripes across the field in wheat and greens.
      const stripe = Math.sin((d.dot(new THREE.Vector3(0.3, 0.8, 0.5)) * R) * 2.2);
      return stripe > 0.3 ? 0xdcb75a : stripe > -0.4 ? 0x9b6b43 : 0x8fb35a;
    }
    return null;
  },
  blocked: (d) => near(d, PLACES.farms, 12) || near(d, PLACES.keep, 9) || near(d, PLACES.mine, 7) || near(d, PLACES.wharf, 6) || near(d, PLACES.peak, MOUNTAIN_R - 3) || near(d, PLACES.dungeon, 8),
};

/** Roads from the Bazaar out to every village, and the Dragon Road to Mount Cinder. */
export function realmPaving(): Paving[] {
  const gateEnd = REALM.dir(0.82, deg(DRAGON_ROAD_PHI));
  const road = (from: THREE.Vector3, to: THREE.Vector3, width = 3.6): Paving => ({ from, to, width });
  return [
    road(gateEnd, PLACES.keep, 4.4),
    road(PLACES.keep, PLACES.dungeon, 4.4),
    road(REALM.dir(0.45, deg(205)), PLACES.farms),
    road(REALM.dir(0.45, deg(330)), PLACES.wood),
    road(REALM.dir(0.45, deg(28)), PLACES.wharf),
    road(PLACES.farms, PLACES.mine),
    road(PLACES.mine, P(Math.PI - 0.75, 200), 3),
    { dir: PLACES.keep.clone(), radius: 6 },
    { dir: PLACES.wharf.clone(), radius: 4 },
    { dir: PLACES.dungeon.clone(), radius: 4.5 },
  ];
}

/** Flat pads under each village. */
export function realmPads() {
  return [
    { name: 'farms', dir: PLACES.farms.clone(), radius: 12 },
    { name: 'wood', dir: PLACES.wood.clone(), radius: 5 },
    { name: 'wharf', dir: PLACES.wharf.clone(), radius: 6 },
    { name: 'keep', dir: PLACES.keep.clone(), radius: 9 },
    { name: 'mine', dir: PLACES.mine.clone(), radius: 7 },
    { name: 'dungeon', dir: PLACES.dungeon.clone(), radius: 6 },
  ];
}

/** Local offset (metres across the ground) from a place. */
export const near2 = (place: THREE.Vector3, dx: number, dz: number) => offsetDir(place, dx, dz);

// ------------------------------------------------------------ people & things

export const REALM_NPCS: NpcSpec[] = [
  { id: 'odo', name: 'Farmer Odo', color: '#9b7a2a', kind: 'Barbarian', opts: { hideHeadgear: true }, at: [0, 0], dir: near2(PLACES.farms, 2.5, -1.5),
    chatter: ['Turnips don’t pull themselves. Usually.'], shouts: ['Fresh crops! Free advice!'] },
  { id: 'fern', name: 'Fern the Hunter', color: '#4f7a3a', kind: 'Rogue', opts: { keep: ['1H_Crossbow'] }, at: [0, 0], dir: near2(PLACES.wood, 2.2, 2.4),
    chatter: ['Whisperwood talks if you stop walking long enough.'] },
  { id: 'gil', name: 'Gil the Fisher', color: '#3b6fb0', kind: 'Barbarian', opts: { hideHeadgear: true, keep: ['Mug'] }, at: [0, 0], dir: near2(PLACES.wharf, 0.5, 3.4),
    chatter: ['The lake gives. The lake takes. Mostly it takes my lunch.'] },
  { id: 'pim', name: 'Sir Pim', color: '#b8892f', kind: 'Knight', opts: { hideHeadgear: false, keep: ['1H_Sword', 'Round_Shield'] }, at: [0, 0], dir: near2(PLACES.keep, 0, -6.5),
    chatter: ['The Keep has stood a thousand years. Its banner, apparently, has not.'] },
  { id: 'maud', name: 'Sister Maud', color: '#7b52b8', kind: 'Mage', opts: { hideHeadgear: true }, at: [0, 0], dir: near2(PLACES.keep, 9, -2),
    chatter: ['Bless your blade, my lord. Point it at the dragon, not at me.', 'Mount Cinder has been warm for a week. That is never good.'] },
  { id: 'brick', name: 'Brick the Miner', color: '#8a3324', kind: 'Barbarian', opts: { hideHeadgear: false, keep: ['1H_Axe'] }, at: [0, 0], dir: near2(PLACES.mine, 3, 2),
    chatter: ['Ironvein runs deep. So do I.'] },
];

export const REALM_PICKUPS: PickupSpec[] = [
  { key: 'turnip1', item: 'turnips', at: [0, 0], dir: near2(PLACES.farms, -6, 4), quest: 'turnips' },
  { key: 'turnip2', item: 'turnips', at: [0, 0], dir: near2(PLACES.farms, 5.5, 6.5), quest: 'turnips' },
  { key: 'turnip3', item: 'turnips', at: [0, 0], dir: near2(PLACES.farms, -4, -7), quest: 'turnips' },
  { key: 'lostquiver', item: 'lostquiver', at: [0, 0], dir: near2(PLACES.wood, -9, -6), quest: 'arrows' },
  { key: 'banner', item: 'banner', at: [0, 0], dir: near2(PLACES.mine, -8, 6), quest: 'banner' },
  { key: 'lantern', item: 'lantern', at: [0, 0], dir: near2(PLACES.mine, -4, -5), quest: 'lantern' },
  // Loose coins along the roads.
  { key: 'rc1', item: 'coins', at: [0, 0], dir: P(0.75, 205) }, { key: 'rc2', item: 'coins', at: [0, 0], dir: P(0.9, 330) },
  { key: 'rc3', item: 'coins', at: [0, 0], dir: P(0.8, 28) }, { key: 'rc4', item: 'coins', at: [0, 0], dir: P(1.2, DRAGON_ROAD_PHI) },
  { key: 'rc5', item: 'coins', at: [0, 0], dir: P(1.5, 230) }, { key: 'rc6', item: 'coins', at: [0, 0], dir: P(2.1, DRAGON_ROAD_PHI) },
  { key: 'rc7', item: 'coins', at: [0, 0], dir: near2(PLACES.keep, -4, 3) }, { key: 'rc8', item: 'coins', at: [0, 0], dir: near2(PLACES.wharf, -3, -2) },
];

export type RealmBuild = {
  zones: ZoneDef[];
  dungeonDoor: THREE.Vector3;
  scarecrows: THREE.Object3D[];
};

export function buildRealm(planet: Planet): RealmBuild {
  const rand = createSeededRandom(2468);
  const ground = (d: THREE.Vector3, lift = 0) => planet.surfacePoint(d, lift);
  const put = (key: ModelKey, d: THREE.Vector3, opts: { height?: number; face?: THREE.Vector3; yaw?: number; collide?: number; sink?: number } = {}) => {
    const obj = model(key, { height: opts.height });
    obj.position.copy(ground(d, -(opts.sink ?? 0.05)));
    faceToward(obj, opts.face ?? ground(MARKET), opts.yaw ?? 0);
    planet.group.add(obj);
    const size = obj.userData.size as THREE.Vector3;
    const r = opts.collide ?? Math.max(size.x, size.z) * 0.45;
    if (r > 0) planet.addCollider(new THREE.CylinderGeometry(r, r, size.y + 0.5, 10).translate(0, size.y / 2, 0), obj);
    return obj;
  };
  const at = (place: THREE.Vector3, dx: number, dz: number) => near2(place, dx, dz);

  // --- Hollow Farms: farmhouses, fences, a scarecrow, produce ---
  const f = PLACES.farms;
  put('homeA_yellow', at(f, -3, -3.5), { height: 4.6, face: ground(f) });
  put('homeB_red', at(f, 4.5, -4), { height: 4.4, face: ground(f) });
  put('watermill', at(f, 0, 10.5), { height: 5.5, face: ground(f) });
  for (const [x, z] of [[-2, 2], [1.5, 2.4], [3.6, 0.8]] as const) put(['crateCarrots', 'crateTomatoes', 'cratePotatoes'][Math.abs(x | 0) % 3] as ModelKey, at(f, x, z), { height: 0.7, collide: 0.45 });
  put('barrels', at(f, -4.4, 0.6), { height: 1.2 });
  const scarecrows: THREE.Object3D[] = [];
  const straw = createInkMaterial({ color: 0xd9b45a, lineBreak: 0.1 });
  const cloth = createInkMaterial({ color: 0xb0442f, lineBreak: 0.1 });
  const wood = createInkMaterial({ color: 0x7a4f33, lineBreak: 0.1 });
  for (const [x, z] of [[-7, 2], [6, -8.5]] as const) {
    const g = new THREE.Group();
    g.add(new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.08, 2.2, 6).translate(0, 1.1, 0), wood));
    const arm = new THREE.Mesh(new THREE.CylinderGeometry(0.05, 0.05, 1.6, 6), wood);
    arm.rotation.z = Math.PI / 2;
    arm.position.y = 1.6;
    g.add(arm, new THREE.Mesh(new THREE.BoxGeometry(0.6, 0.7, 0.3).translate(0, 1.45, 0), cloth));
    g.add(new THREE.Mesh(new THREE.SphereGeometry(0.24, 10, 8).translate(0, 2.05, 0), straw));
    g.add(new THREE.Mesh(new THREE.ConeGeometry(0.4, 0.35, 10).translate(0, 2.35, 0), straw));
    g.traverse((o) => { o.castShadow = o.receiveShadow = true; });
    g.position.copy(ground(at(f, x, z)));
    faceToward(g, ground(MARKET));
    planet.group.add(g);
    scarecrows.push(g);
  }

  // --- Whisperwood: a lumber mill in a dense forest ---
  const w = PLACES.wood;
  put('lumbermill', at(w, -1.5, -1), { height: 5, face: ground(MARKET) });
  for (let i = 0, n = 0; i < 400 && n < 70; i++) {
    const a = rand() * Math.PI * 2, r = 5.5 + Math.sqrt(rand()) * 17;
    const d = at(w, Math.cos(a) * r, Math.sin(a) * r);
    // Leave the road into the wood clear.
    if (arcDist(d, REALM.dir(REALM.theta(d), deg(330))) < 2.5 && REALM.theta(d) < REALM.theta(w)) continue;
    planet.addTree(d, 0.9 + rand() * 0.8, rand() < 0.65, 1 + Math.floor(rand() * 2), rand() * 6);
    n++;
  }

  // --- Lakeside Wharf: a jetty, a mill, fisher's huts ---
  const wh = PLACES.wharf;
  put('homeB_blue', at(wh, -4, -1.5), { height: 4.2, face: ground(PLACES.lake) });
  put('homeA_green', at(wh, 3.8, -2.2), { height: 4.2, face: ground(PLACES.lake) });
  put('barrel', at(wh, 1.8, 2.2), { height: 0.9, collide: 0.4 });
  put('crates', at(wh, -1.6, 2.6), { height: 1.0 });
  const plank = createInkMaterial({ color: 0x8a6040, lineBreak: 0.12 });
  const jetty = new THREE.Group();
  for (let i = 0; i < 9; i++) jetty.add(new THREE.Mesh(new THREE.BoxGeometry(2.2, 0.16, 0.85).translate(0, 0, i * 0.92), plank));
  for (const x of [-1, 1]) for (let i = 0; i < 9; i += 2) jetty.add(new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 1.6, 6).translate(x, -0.7, i * 0.92), wood));
  jetty.traverse((o) => { o.castShadow = o.receiveShadow = true; });
  jetty.position.copy(ground(at(wh, 0, 4.4)).normalize().multiplyScalar(R + 0.15));
  faceToward(jetty, ground(PLACES.lake));
  planet.group.add(jetty);

  // --- The Old Keep: castle, towers, a church ---
  const k = PLACES.keep;
  put('keep', at(k, 0, 3), { height: 10, face: ground(MARKET) });
  put('towerRed', at(k, -6.5, 1), { height: 7.5, face: ground(MARKET) });
  put('towerRed', at(k, 6.5, 1), { height: 7.5, face: ground(MARKET) });
  put('church', at(k, 10, 2), { height: 6.5, face: ground(MARKET) });
  for (const x of [-3, 3]) put('bannerRed', at(k, x, -4.5), { height: 3, collide: 0.3 });

  // --- Ironvein Mine: the mine head, ore crates, torches ---
  const m = PLACES.mine;
  put('mine', at(m, 0, -2), { height: 5, face: ground(MARKET) });
  put('boxes', at(m, 3.6, -0.5), { height: 1.2 });
  put('barrels', at(m, -3.2, 0.5), { height: 1.2 });
  for (const x of [-2, 2]) put('torch', at(m, x, 1.8), { height: 1.9, collide: 0.15 });
  for (let i = 0; i < 10; i++) {
    const s = 0.6 + rand() * 1.3;
    const rock = new THREE.Mesh(new THREE.DodecahedronGeometry(s, 0), createInkMaterial({ color: 0x8a7f74, lineBreak: 0.25 }));
    rock.position.copy(ground(at(m, (rand() - 0.5) * 18, -6 - rand() * 6), -s * 0.3));
    rock.castShadow = rock.receiveShadow = true;
    planet.group.add(rock);
  }

  // --- Mount Cinder: the dungeon door, flanked by pillars and torches ---
  const dd = PLACES.dungeon;
  put('dDoorway', dd, { height: 5, face: ground(MARKET), collide: 0 });
  // Rock shoulders either side, so the doorway reads as cut into the mountain.
  const basalt = createInkMaterial({ color: 0x4f4743, lineBreak: 0.2 });
  for (const [x, z, s] of [[-4.6, 1.5, 2.6], [4.6, 1.5, 2.8], [-2.8, 3.5, 3.2], [3, 3.8, 3.4], [0, 5.5, 4.2]] as const) {
    const r = new THREE.Mesh(new THREE.DodecahedronGeometry(s, 0), basalt);
    r.position.copy(ground(at(dd, x, z), s * 0.15));
    r.castShadow = r.receiveShadow = true;
    planet.group.add(r);
    planet.addCollider(new THREE.SphereGeometry(s * 0.9, 8, 6), r);
  }
  for (const x of [-2.6, 2.6]) {
    put('dPillar', at(dd, x, -0.6), { height: 4.2, collide: 0.5, face: ground(MARKET) });
    put('torch', at(dd, x * 1.25, -1.8), { height: 2.1, collide: 0.15 });
  }
  put('swordShield', at(dd, -4.4, -2.6), { height: 1.2, yaw: 0.6 }); // a fallen hero's gear
  // A warm glow pulsing from inside the doorway.
  const glow = new THREE.Mesh(new THREE.PlaneGeometry(2.2, 3.2), createFlatMaterial(0xff7a2a, 0));
  glow.position.copy(ground(dd, 1.6));
  faceToward(glow, ground(MARKET, 1.6));
  planet.group.add(glow);


  const zones: ZoneDef[] = [
    { name: 'The Dragon’s Door', dir: dd.clone(), radius: 6, ambience: 'wind' },
    { name: 'Mount Cinder', dir: PLACES.peak.clone(), radius: MOUNTAIN_R + 2, ambience: 'wind' },
    { name: 'Hollow Farms', dir: f.clone(), radius: 12, ambience: 'forest' },
    { name: 'Whisperwood', dir: w.clone(), radius: 14, ambience: 'forest' },
    { name: 'Lakeside Wharf', dir: wh.clone(), radius: 8, ambience: 'water' },
    { name: 'The Old Keep', dir: k.clone(), radius: 11, ambience: 'wind' },
    { name: 'Ironvein Mine', dir: m.clone(), radius: 9, ambience: 'forge' },
  ];
  return { zones, dungeonDoor: ground(dd), scarecrows };
}

